// SPV Pool v1.0 PR 5 — display-name resolution for Investor / service-provider
// pages. The Investor + provider DTOs reference a Person or Entity by id;
// identity (name) lives on those counterparty rows. Rather than an N+1 of
// per-row GETs, the list pages fetch the Person + Entity directories once and
// build lookup maps — the same pattern PoolListPage uses for trust names.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { entitiesApi, personsApi } from "@/api/counterparties";

export function usePersonNameMap(enabled = true) {
  const query = useQuery({
    queryKey: ["counterparties", "persons", "name-map"],
    queryFn: () => personsApi.list({ pageSize: 200, includeArchived: true }),
    enabled,
  });
  const map = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of query.data?.data ?? []) m.set(p.id, p.displayName);
    return m;
  }, [query.data]);
  return { map, isLoading: query.isLoading };
}

export function useEntityNameMap(enabled = true) {
  const query = useQuery({
    queryKey: ["counterparties", "entities", "name-map"],
    queryFn: () => entitiesApi.list({ pageSize: 200, includeArchived: true }),
    enabled,
  });
  const map = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of query.data?.data ?? []) m.set(e.id, e.legalEntityName);
    return m;
  }, [query.data]);
  return { map, isLoading: query.isLoading };
}

/**
 * Resolves an investor's display name from the linked Person (individual
 * accredited investors) or Entity (institutions / funds / family offices).
 */
export function resolveInvestorName(
  personId: string | null,
  entityId: string | null,
  personNames: Map<string, string>,
  entityNames: Map<string, string>,
): string {
  if (personId) return personNames.get(personId) ?? "Unnamed investor";
  if (entityId) return entityNames.get(entityId) ?? "Unnamed investor";
  return "Unnamed investor";
}
